import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import { Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";

import { withRouter, Link } from "react-router-dom";
import Devices from "@mui/icons-material/Devices";
import PhaseColourHelper from "./PhaseColourHelper";


const PREFIX = "TableCell";

const classes = {
  greenBox: `${PREFIX}-greenBox`,
  redBox: `${PREFIX}-redBox`,
};

const Root = styled("tr")({
  [`& .${classes.greenBox}`]: {
    textTransform: "uppercase",
    background: "#e5f9ed",
    margin: "auto !important",
    padding: "2px 7px",
    color: "#1abc943",
    display: "flex",
    alignItems: "center",
    gap: 4,
    justifyContent: "center",
    borderRadius: 6,
    whiteSpace: "nowrap",
    fontSize: "11px !important",
    fontWeight: "700 !important",
  },
  [`& .${classes.redBox}`]: {
    textTransform: "uppercase",
    background: "#fffbf2",
    padding: "2px 7px",
    margin: "auto !important",
    color: "#f4772e",
    display: "flex",
    alignItems: "center",
    gap: 4,
    justifyContent: "center",
    borderRadius: 6,
    whiteSpace: "nowrap",
    fontSize: "11px !important",
    fontWeight: "700 !important",
  },
});

const getEnvironmentIcon = (value) => {
  if (value === "Webcam")
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <DesktopWindowsIcon style={{ color: "rgb(99,115,129)" }} fontSize="small" />
        <Typography variant="body2" component="span">
          Webcam
        </Typography>
      </div>
    );

  if (value === "Mobile")
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SmartphoneRoundedIcon fontSize="small" style={{ color: "rgb(99,115,129)" }} />
        <Typography variant="body2" component="span">
          Mobile
        </Typography>
      </div>
    );

  if (value === "Lab" || value === "All devices")
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Devices fontSize="small" style={{ color: "rgb(99,115,129)" }} />
        <Typography variant="body2" component="span">
          All devices
        </Typography>
      </div>
    );

  return "Unknown";
};

const TableCell = (props) => {
  const {
    id,
    title,
    environment,
    media,
    phase,
    phase_status,
    phase_colour,
    participants_requested,
    participants_returned,
    data_collection_end,
    data_collection_start,
    qualityChecksEnabled,
    systemPauseOverride,
    selected,
    onSelectChange,
  } = props;
  const intId = parseInt(id, 10)

  console.log("Phase colour for study:", title, phase_colour);

  return (
    <Root>
      <td style={{ textAlign: "center" }}>
        <input
          type="checkbox"
          checked={!!selected}
          onChange={(e) => onSelectChange?.(e, id)}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "16px",
            height: "16px",
            display: "inline-block",
            visibility: "visible",
            position: "relative",
            margin: "0 auto",
          }}
        />
      </td>

      {/* study id column */}
      <td>
        <Typography
          variant="body2"
          component="span"
          style={{
            fontWeight: 400,
            whiteSpace: "nowrap",
          }}
        >
          {id}
        </Typography>
      </td>

      <td style={{ paddingLeft: 32 }}>
        <Typography variant="subtitle2" component="span">
          <Tooltip placement="right" title="View study details">
            <Link to={`/my_studies/overview/${intId}`}>{title}</Link>
          </Tooltip>
        </Typography>
      </td>



      <td>{getEnvironmentIcon(environment)}</td>

      <td>
        <Typography variant="body2" component="span">
          {media}
        </Typography>
      </td>

      

      <td style={{ textAlign: "center" }}>
        <Tooltip
          placement="top"
          title={qualityChecksEnabled ? "Quality checks enabled" : "Quality checks disabled"}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {qualityChecksEnabled ? (
              <CheckIcon style={{ color: "rgb(0, 171, 85)" }} fontSize="small" />
            ) : (
              <ClearIcon style={{ color: "rgb(244, 67, 54)" }} fontSize="small" />
            )}
          </span>
        </Tooltip>
      </td>

      <td style={{ textAlign: "center" }}>
        <Tooltip
          placement="top"
          title={systemPauseOverride ? "System pause override enabled" : "System pause override disabled"}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {systemPauseOverride ? (
              <CheckIcon style={{ color: "rgb(0, 171, 85)" }} fontSize="small" />
            ) : (
              <ClearIcon style={{ color: "rgb(244, 67, 54)" }} fontSize="small" />
            )}
          </span>
        </Tooltip>
      </td>

      {/*phase column*/}
      <td>
        <Typography
          variant="body2"
          component="span"
          style={{
            backgroundColor: "#f2f2f2",
            padding: "2px 8px",
            borderRadius: 6,
            fontWeight: 600,
            color: "#333",
            whiteSpace: "nowrap",
          }}
        >
          {phase || "—"}
        </Typography>
      </td>


      {/*phase status column */}
      <td>
        <Typography
          variant="body2"
          component="span"
          style={{
            backgroundColor: "#f2f2f2",
            color: "#333",
            padding: "2px 8px",
            borderRadius: 6,
            whiteSpace: "nowrap",
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        >
          {phase_status || "—"}
        </Typography>
      </td>

      <td>
        <PhaseColourHelper phaseColour={phase_colour} />
      </td>

      <td style={{ textAlign: "right" }}>
        <Tooltip
          placement="top"
          title={
            <>
              <p>{participants_requested} participants requested</p>
              <p>{participants_returned} returned so far</p>
            </>
          }
        >
          <Typography noWrap variant="body2" component="span">
            {`${participants_returned} / ${participants_requested}`}
          </Typography>
        </Tooltip>
      </td>
      <td className="RemoveColumnWhen1200">
        <Typography style={{ whiteSpace: "nowrap" }} variant="body2" component="span">
          {data_collection_start}
        </Typography>
      </td>
      <td className="RemoveColumnWhen1350">
        <Typography style={{ whiteSpace: "nowrap" }} variant="body2" component="span">
          {data_collection_end}
        </Typography>
      </td>
    </Root>
  );
};

TableCell.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  environment: PropTypes.string,
  media: PropTypes.string,
  phase: PropTypes.string,
  phase_status: PropTypes.string,
  phase_colour: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  participants_requested: PropTypes.number,
  participants_returned: PropTypes.number,
  data_collection_end: PropTypes.string,
  data_collection_start: PropTypes.string,
  qualityChecksEnabled: PropTypes.bool,
  systemPauseOverride: PropTypes.bool,
  onToggleQualityChecks: PropTypes.func,
  disableQualityChecksToggle: PropTypes.bool,
  selected: PropTypes.bool,
  onSelectChange: PropTypes.func,
};

export default withRouter(TableCell);